import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCircle, ChevronDown, CircleCheck, CirclePlay, History, Layers, MessageSquareWarning, Repeat, SkipForward, TriangleAlert, X, XCircle, type LucideIcon } from 'lucide-react'
import { toast } from 'sonner'
import { chatsApi, type WorkflowRunSummary } from '@/api/chats'
import { workflowRunsHitlApi } from '@/api/workflowRunsHitl'
import type { WorkflowChangeSet, WorkflowRunChangeSets } from '@/api/workflowChangeSets'
import { client } from '@/api/client'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { useTenantKey } from '@/hooks/useTenantKey'
import { useApprovePhase, useAuth, useEditChangeSetItemProposedContent, useRejectPhase, useSetChangeSetItemDecision, useSetChangeSetRoundDecision, useSecurityReviewApprovalBlock, useWorkflowRunChangeSets, useNothingStagedApprovalGuard } from '@/hooks'
import { changeSetItemTitle } from '@/lib/changeSetItemTitle'
import { currentChangeSetOf, isReviewableRound, stagedCountLabel, stagedRoundOf } from '@/lib/changeSetRoundDiff'
import { formatDurationMs, formatElapsedTime } from '@/lib/timeFormatters'
import { cn, tryParseJson } from '@/lib/utils'
import { Permissions } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TooltipProvider } from '@/components/ui/tooltip'
import { RejectPhaseModal } from '../workflows/runs/RejectPhaseModal'
import { hasCopyableContent, parseOutputsJson, renderOutputValue } from '../workflows/nodes/nodeOutputValue'
import { CopyButton } from '@/components/ui/copy-button'
import { ChangeSetDiffDialog, type ChangeSetDiffDialogMode } from '../workflows/chat/ChangeSetDiffDialog'
import { RoundDecisionActions } from '../workflows/chat/RoundDecisionActions'
import { ApproveNoChangesDialog } from '../workflows/shared/ApproveNoChangesDialog'
import { ChangeSetStatusBadge } from '../workflows/shared/ChangeSetStatusBadge'
import { WorkflowStatusBadge } from '../workflows/shared/WorkflowStatusBadge'
import type { ChatRunStreamState } from '@/hooks/useChatRunStream'
import type { WorkflowRunStatus } from '@/types'


interface Props {
  projectId: string
  chatId: string
  runStreamState: ChatRunStreamState
  isSidebarOpen: boolean
}

interface RunTabState {
  id: string
  kind: 'overview' | 'changes' | 'node'
  nodeId?: string
}

const OVERVIEW_TAB: RunTabState = { id: 'overview', kind: 'overview' }
const CHANGES_TAB: RunTabState = { id: 'changes', kind: 'changes' }

export function ChatRunSidebar({ projectId, chatId, runStreamState, isSidebarOpen }: Props) {
  const [tabs, setTabs] = useState<RunTabState[]>([OVERVIEW_TAB])
  const [activeTabId, setActiveTabId] = useState('overview')
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const tenantKey = useTenantKey()

  useEffect(() => {
    setTabs([OVERVIEW_TAB])
    setActiveTabId('overview')
    setSelectedRunId(null)
  }, [chatId])

  const { data: runs } = useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'runs'),
    queryFn: () => chatsApi.getRuns(projectId, chatId),
    staleTime: 5_000,
  })

  useEffect(() => {
    if (!runs || runs.length === 0) {
      setSelectedRunId(null)
      return
    }
    if (selectedRunId && runs.some((r) => r.runId === selectedRunId)) return
    const active = runs.find((r) => !r.isTerminal) ?? runs[0]
    setSelectedRunId(active.runId)
  }, [runs, selectedRunId])

  const selectedRunForChangeSets = runs?.find((r) => r.runId === selectedRunId) ?? runs?.[0]
  const { data: changeSetData } = useWorkflowRunChangeSets(
    projectId,
    selectedRunForChangeSets?.runId ?? null,
    !!selectedRunForChangeSets,
  )

  if (!runs || runs.length === 0) return null

  const selectedRun = runs.find((r) => r.runId === selectedRunId) ?? runs[0]
  const hasChangeSets = (changeSetData?.changeSets.length ?? 0) > 0
  const pendingChangeSet = changeSetData?.changeSets.find((c) => c.status === 'Pending')
  const pendingItemCount = pendingChangeSet?.items.length ?? 0

  const displayTabs: RunTabState[] = [
    OVERVIEW_TAB,
    ...(hasChangeSets ? [CHANGES_TAB] : []),
    ...tabs.filter((t) => t.kind === 'node'),
  ]
  const activeTab = displayTabs.find((t) => t.id === activeTabId) ?? OVERVIEW_TAB

  const openNodeTab = (nodeId: string) => {
    const tabId = `node-${nodeId}`
    setTabs((prev) => (prev.some((t) => t.id === tabId) ? prev : [...prev, { id: tabId, kind: 'node', nodeId }]))
    setActiveTabId(tabId)
  }

  const closeTab = (tabId: string) => {
    if (tabId === 'overview') return
    setTabs((prev) => prev.filter((t) => t.id !== tabId))
    setActiveTabId((cur) => (cur === tabId ? 'overview' : cur))
  }

  if (!isSidebarOpen) {
    return null
  }

  return (
    <div className="w-[380px] shrink-0 grow-0 min-h-0 overflow-hidden border rounded-xl bg-panel">
      <aside className="flex h-full w-full flex-col bg-muted/20">
      <div className="flex items-center justify-between border-b bg-panel px-3 py-2">
        <div className="min-w-0 flex-1">
          <RunSwitcher runs={runs} selectedRunId={selectedRun.runId} onChange={setSelectedRunId} />
          <div className="mt-1.5 flex items-center gap-1.5">
            <WorkflowStatusBadge status={selectedRun.status as WorkflowRunStatus} />
            <span className="text-[10px] text-muted-foreground">
              {formatElapsedTime(selectedRun.startedAt, selectedRun.completedAt)}
              {selectedRun.triggeredByName && <> · by {selectedRun.triggeredByName}</>}
            </span>
          </div>
        </div>
      </div>

      <div className="p-2">
        <div role="tablist" className="flex w-fit max-w-full shrink-0 gap-1 overflow-x-auto rounded-xl border bg-panel p-1 text-sm">
          {displayTabs.map((t) => {
            const isActive = t.id === activeTabId
            const label =
              t.kind === 'overview' ? 'Overview'
              : t.kind === 'changes' ? 'Changes'
              : nodeNameById(selectedRun, runStreamState, t.nodeId!) ?? 'Node'
            return (
              <div
                key={t.id}
                role="tab"
                aria-selected={isActive}
                className={cn(
                  'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1',
                  isActive ? 'bg-primary font-semibold text-primary-foreground' : 'text-muted-foreground hover:bg-background/60',
                )}
                onClick={() => setActiveTabId(t.id)}
              >
                <span className="max-w-[150px] truncate">{label}</span>
                {t.kind === 'changes' && pendingChangeSet && (
                  <span className={cn(
                    'rounded-full px-1.5 text-[10px] font-bold leading-4',
                    isActive ? 'bg-primary-foreground text-brand-panel dark:text-primary' : 'bg-button text-button-foreground',
                  )}>
                    {pendingItemCount}
                  </span>
                )}
                {t.kind === 'node' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      closeTab(t.id)
                    }}
                    className="ml-1 rounded p-0.5 hover:bg-muted"
                    title="Close tab"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-panel">
        {activeTab?.kind === 'overview' && (
          <OverviewTab
            projectId={projectId}
            runId={selectedRun.runId}
            runStreamState={runStreamState}
            onNodeClick={openNodeTab}
            changeSetData={changeSetData}
            onOpenChanges={hasChangeSets ? () => setActiveTabId('changes') : undefined}
          />
        )}
        {activeTab?.kind === 'changes' && (
          <ChangesTab
            projectId={projectId}
            runId={selectedRun.runId}
            triggeredBy={selectedRun.triggeredBy}
            changeSetData={changeSetData}
            onGoToOverview={() => setActiveTabId('overview')}
          />
        )}
        {activeTab?.kind === 'node' && (
          <NodeDetailTab
            projectId={projectId}
            runId={selectedRun.runId}
            nodeId={activeTab.nodeId!}
            runStreamState={runStreamState}
          />
        )}
      </div>
      </aside>
    </div>
  )
}

function RunSwitcher({
  runs,
  selectedRunId,
  onChange,
}: {
  runs: WorkflowRunSummary[]
  selectedRunId: string
  onChange: (id: string) => void
}) {
  return (
    <Select value={selectedRunId} onValueChange={onChange}>
      <SelectTrigger aria-label="Select run" className="mt-0.5 h-auto w-full truncate border-none bg-transparent p-0 text-sm font-semibold shadow-none focus:ring-0 focus:ring-offset-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {runs.map((r) => {
          const time = r.startedAt
            ? new Date(r.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : 'pending'
          const label = r.workflowDefinitionName ? `${r.workflowDefinitionName} — ${time}` : time
          return (
            <SelectItem key={r.runId} value={r.runId}>
              <span className="flex min-w-0 items-center gap-1.5">
                {r.isTerminal
                  ? <CircleCheck className="h-3.5 w-3.5 shrink-0" />
                  : <CirclePlay className="h-3.5 w-3.5 shrink-0" />}
                <span className="shrink-0 text-sm uppercase tracking-wide">Run:</span>
                <span className="truncate">{label}</span>
              </span>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

interface RunDetailPayload {
  id: string
  status: string
  phases: {
    id: string
    name: string
    displayName: string | null
    status: string
    approvalMessage: string | null
    branches: {
      id: string
      nodes: { id: string; name: string; taskType: string; status: string; durationMs?: number | null; feedbackEntry: boolean }[]
    }[]
  }[]
}

function OverviewTab({
  projectId,
  runId,
  runStreamState,
  onNodeClick,
  changeSetData,
  onOpenChanges,
}: {
  projectId: string
  runId: string
  runStreamState: ChatRunStreamState
  onNodeClick: (nodeId: string) => void
  changeSetData: WorkflowRunChangeSets | undefined
  onOpenChanges?: () => void
}) {
  const tenantKey = useTenantKey()
  const { data: run, isLoading } = useQuery<RunDetailPayload>({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'detail'),
    queryFn: () =>
      client
        .get<RunDetailPayload>(`/projects/${projectId}/workflow-runs/${runId}`)
        .then((r) => r.data),
    staleTime: 2_000,
  })
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set())

  const togglePhase = (phaseId: string) => {
    setCollapsedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(phaseId)) next.delete(phaseId)
      else next.add(phaseId)
      return next
    })
  }

  if (isLoading) return <div className="p-3 text-sm text-muted-foreground">Loading run…</div>
  if (!run) return <div className="p-3 text-sm text-muted-foreground">No run data.</div>

  return (
    <div className="p-2">
      <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Phases
      </div>
      {run.phases.map((phase) => {
        const effectivePhaseStatus =
          phase.status === "Running" &&
          phase.branches.some((b) =>
            b.nodes.some(
              (n) =>
                (runStreamState.nodeStatuses[n.id] ?? n.status) ===
                "AwaitingUserInput",
            ),
          )
            ? "AwaitingUserInput"
            : phase.status;
        const isCollapsed = collapsedPhases.has(phase.id);
        return (
        <div key={phase.id} className={`mb-3 rounded-lg border p-2 ${borderClassForStatus(effectivePhaseStatus)}`}>
          <button
            type="button"
            onClick={() => togglePhase(phase.id)}
            className="flex w-full items-center justify-between px-1 py-1 text-xs font-bold text-foreground"
          >
            <span className="flex items-center gap-2">
              <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')} />
              <Layers className="h-3.5 w-3.5 shrink-0" />
              {phase.displayName || phase.name}
            </span>
            <StatusPill status={effectivePhaseStatus} />
        </button>
        {!isCollapsed && (
          <>
            {phase.status === "AwaitingApproval" && (
              <GateApprovalBlock
                projectId={projectId}
                runId={runId}
                phaseId={phase.id}
                phaseName={phase.displayName || phase.name}
                approvalMessage={phase.approvalMessage}
                pendingChangeSet={changeSetData?.changeSets.find(
                  (c) => c.status === "Pending",
                )}
                stagedRound={changeSetData && stagedRoundOf(changeSetData.changeSets)}
                onOpenChanges={onOpenChanges}
              />
            )}
            <div className="flex flex-col gap-1">
              {phase.branches.flatMap((branch) =>
                branch.nodes.map((node) => {
                  const liveStatus =
                    runStreamState.nodeStatuses[node.id] ?? node.status;
                  return (
                    <button
                      key={node.id}
                      type="button"
                      onClick={() => onNodeClick(node.id)}
                      className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs"
                    >
                      <NodeStatusIcon status={liveStatus} />
                      <span className="flex-1 truncate font-medium">
                        {node.name}
                      </span>
                      {node.durationMs != null && (
                        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                          {formatDurationMs(node.durationMs)}
                        </span>
                      )}
                      {node.feedbackEntry && (
                        <span
                          title="feedbackEntry: gate feedback rewinds the loop to this node"
                          className="flex shrink-0 items-center gap-0.5 rounded-full border border-purple-foreground bg-purple px-1 text-[9px] font-semibold uppercase text-purple-foreground"
                        >
                          <Repeat className="h-2.5 w-2.5" /> entry
                        </span>
                      )}
                      <NodeStatusBadgeIcon status={liveStatus} />
                    </button>
                  );
                }),
              )}
            </div>
          </>
        )}
        </div>
        ) 
      })}
      <p className="mt-2 px-2 text-[11px] italic text-muted-foreground">
        Click any node to open Inputs / Outputs / Logs.
      </p>
    </div>
  )
}

const MAX_LISTED_SKIPPED_ITEMS = 5

function SkippedItemsNotice({
  projectId,
  runId,
  nodeId,
  proposedCount,
}: {
  projectId: string
  runId: string
  nodeId: string
  proposedCount: number
}) {
  const tenantKey = useTenantKey()
  const { data } = useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId),
    queryFn: () => workflowRunsHitlApi.getNodeDetail(projectId, runId, nodeId),
    enabled: !!projectId,
    staleTime: 2_000,
  })

  const outputs = parseOutputsJson(data?.outputsJson ?? null) ?? []
  const skippedCount = Number(outputs.find((o) => o.key === 'skippedCount')?.value ?? 0)
  if (!skippedCount) return null

  const parsedReasons = tryParseJson(outputs.find((o) => o.key === 'skippedItems')?.value ?? '')
  const reasons = Array.isArray(parsedReasons) ? parsedReasons.filter((r): r is string => typeof r === 'string') : []
  const hiddenCount = Math.max(0, reasons.length - MAX_LISTED_SKIPPED_ITEMS)
  const isSingle = skippedCount === 1

  return (
    <div className="mt-1.5 text-xs leading-relaxed text-foreground" role="status" data-testid="gate-skipped-items-notice">
      <p>
        <b>{skippedCount} of {proposedCount + skippedCount} generated {isSingle ? 'item' : 'items'} could not be processed</b>{' '}
        and {isSingle ? 'was' : 'were'} left out of this change set. Re-run the workflow to generate {isSingle ? 'it' : 'them'} again.
      </p>
      {reasons.length > 0 && (
        <ul className="mt-1 list-disc pl-4">
          {reasons.slice(0, MAX_LISTED_SKIPPED_ITEMS).map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
          {hiddenCount > 0 && <li>…and {hiddenCount} more.</li>}
        </ul>
      )}
    </div>
  )
}

function GateApprovalBlock({
  projectId,
  runId,
  phaseId,
  phaseName,
  approvalMessage,
  pendingChangeSet,
  stagedRound,
  onOpenChanges,
}: {
  projectId: string
  runId: string
  phaseId: string
  phaseName: string
  approvalMessage: string | null
  pendingChangeSet: WorkflowChangeSet | undefined
  stagedRound: ReturnType<typeof stagedRoundOf>
  onOpenChanges?: () => void
}) {
  const { hasPermission } = useAuth(projectId)
  const approvePhase = useApprovePhase(projectId)
  const rejectPhase = useRejectPhase(projectId)
  const [rejectOpen, setRejectOpen] = useState(false)
  const approvalGuard = useNothingStagedApprovalGuard(projectId, runId)
  const [bannerError, setBannerError] = useState<string | null>(null)

  const securityReviewBlock = useSecurityReviewApprovalBlock(projectId, runId, phaseId)
  const pendingFindingsCount = securityReviewBlock.pendingFindingsCount
  const hasUndecidedSecurityFindings = securityReviewBlock.hasUndecidedFindings

  const canApprove = hasPermission(Permissions.Project.Workflow.Approve, projectId)
  const stagedCount = stagedRound?.count ?? 0
  const approveDisabled = approvePhase.isPending || approvalGuard.isChecking || securityReviewBlock.disabled
  const isNoOpApproval = !!pendingChangeSet && stagedCount === 0

  const doApprove = () =>
    approvePhase.mutate(
      { phaseId },
      {
        onSuccess: () => toast.success('Phase approved'),
        onError: (error) => setBannerError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to approve phase'),
      },
    )

  return (
    <div className="mb-2 rounded-md border border-warning-foreground bg-warning p-2.5">
      {bannerError && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-warning-foreground bg-warning py-2 pl-2 pr-3" role="alert">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-warning-foreground"
            onClick={() => setBannerError(null)}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertTriangle className="mt-1.5 h-4 w-4 shrink-0 text-warning-foreground" />
          <p className="text-sm text-warning-foreground">{bannerError}</p>
        </div>
      )}
      <div className="text-[12px] font-semibold flex gap-1 items-center tracking-wide text-warning-foreground">
        <AlertTriangle className="w-3.5 h-3.5" />
        Approval Gate
      </div>
      {approvalMessage?.trim() && (
        <p className="mt-1.5 text-xs leading-relaxed text-foreground" data-testid="gate-approval-message">
          {approvalMessage}
        </p>
      )}
      {pendingChangeSet && (
        <p className="mt-1.5 text-xs leading-relaxed text-foreground">
          Review the proposed backlog changes before publishing. Change set <b>round {pendingChangeSet.round}</b> — <b>{stagedCountLabel(stagedRound, pendingChangeSet)}</b>
          {(!stagedRound || stagedRound.changeSet.id === pendingChangeSet.id) && <> of {pendingChangeSet.items.length} items</>}.{' '}
          {onOpenChanges && (
            <button type="button" className="text-info-foreground underline underline-offset-2 hover:text-info-foreground/70" onClick={onOpenChanges}>
              Review in Changes →
            </button>
          )}
        </p>
      )}
      {pendingChangeSet && (
        <SkippedItemsNotice
          projectId={projectId}
          runId={runId}
          nodeId={pendingChangeSet.stagedByNodeId}
          proposedCount={pendingChangeSet.items.length}
        />
      )}
      {pendingChangeSet?.feedbackAvailable && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed">
          Not right yet? Type feedback in the chat — the agent revises and the gate re-arms.
        </p>
      )}
      {hasUndecidedSecurityFindings && (
        <p className="mt-1.5 text-xs leading-relaxed text-foreground" data-testid="security-review-pending-findings-notice">
          {pendingFindingsCount} of {securityReviewBlock.totalFindingsCount} finding(s) still need a decision — vote in the chat before approving.
        </p>
      )}
      {canApprove && (
        <div className="mt-2 flex items-end justify-end gap-2 pt-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-danger-foreground hover:text-danger-foreground"
            disabled={rejectPhase.isPending}
            onClick={() => setRejectOpen(true)}
          >
            <X className="mr-1 h-3 w-3" />
              Reject
            </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-success-foreground hover:text-success-foreground"
            disabled={approveDisabled}
            title={
              securityReviewBlock.reason
                ?? approvalGuard.reason
                ?? (isNoOpApproval ? 'No changes are staged — approving applies nothing and continues the workflow' : undefined)
            }
            onClick={() => approvalGuard.guardApproval(runId, doApprove)}
          >
            <Check className="mr-1 h-3 w-3" />
              Approve
            </Button>
        </div>
      )}
      <RejectPhaseModal
        phaseName={phaseName}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={(reason) =>
          rejectPhase.mutate(
            { phaseId, reason },
            {
              onSuccess: () => {
                toast.success('Phase rejected')
                setRejectOpen(false)
              },
              onError: (error) => {
                setRejectOpen(false)
                setBannerError(getErrorMessage(error) ?? 'Failed to reject phase')
              },
            },
          )
        }
        isPending={rejectPhase.isPending}
      />
      <ApproveNoChangesDialog
        open={approvalGuard.confirmOpen}
        phaseName={phaseName}
        checkFailed={approvalGuard.checkFailed}
        isApproving={approvePhase.isPending}
        onCancel={approvalGuard.cancel}
        onConfirm={approvalGuard.confirm}
      />
    </div>
  )
}

function ChangesTab({
  projectId,
  runId,
  triggeredBy,
  changeSetData,
  onGoToOverview,
}: {
  projectId: string
  runId: string
  triggeredBy: string | null
  changeSetData: WorkflowRunChangeSets | undefined
  onGoToOverview: () => void
}) {
  const { hasPermission, isAdmin, user } = useAuth(projectId)
  const setDecision = useSetChangeSetItemDecision(projectId, runId)
  const setRoundDecision = useSetChangeSetRoundDecision(projectId, runId)
  const editContent = useEditChangeSetItemProposedContent(projectId, runId)
  const [inspector, setInspector] = useState<{ mode: ChangeSetDiffDialogMode; changeSetId: string; itemId: string | null } | null>(null)
  const [bannerError, setBannerError] = useState<string | null>(null)
  const canDecide = hasPermission(Permissions.Project.Workflow.Approve, projectId)
  const isRunInitiator = !!user?.id && !!triggeredBy && user.id.toLowerCase() === triggeredBy.toLowerCase()

  const decisionMutationOptions = {
    onSuccess: () => setBannerError(null),
    onError: (error: unknown) => setBannerError(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Could not update this item. Please try again.'),
  }

  if (!changeSetData || changeSetData.changeSets.length === 0) {
    return <div className="p-3 text-sm italic text-muted-foreground">No change sets in this run.</div>
  }

  const latest = currentChangeSetOf(changeSetData.changeSets)!
  const previous = changeSetData.changeSets.filter((c) => c.id !== latest.id)
  const isPending = latest.status === 'Pending'
  const stagedLabel = stagedCountLabel(stagedRoundOf(changeSetData.changeSets), latest)

  return (
    <div className="p-2 bg-panel">
      {bannerError && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-warning-foreground bg-warning py-2 pl-2 pr-3" role="alert">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-warning-foreground"
            onClick={() => setBannerError(null)}
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </Button>
          <TriangleAlert className="mt-1.5 h-4 w-4 shrink-0 text-warning-foreground" />
          <p className="text-sm text-warning-foreground">{bannerError}</p>
        </div>
      )}
      <div className="px-1 py-1">
        <span className="text-xs font-bold">Proposed changes</span>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold border">
            Round {latest.round}
          </span>
          <ChangeSetStatusBadge status={latest.status} />
          {isPending && canDecide && (
            <TooltipProvider>
              <RoundDecisionActions
                changeSet={latest}
                onDecide={(decision) => setRoundDecision.mutate({ changeSetId: latest.id, decision }, decisionMutationOptions)}
              />
            </TooltipProvider>
          )}
        </div>
      </div>
      <p className="px-1 pb-1.5 text-[11px] text-muted-foreground">
        {latest.items.filter((i) => i.operation === 'Create').length} new ·{' '}
        {latest.items.filter((i) => i.operation === 'Update').length} updated
        {isPending && <> — <b className="text-foreground">{stagedLabel}</b></>}
      </p>

      <div className="flex flex-col gap-1">
        {latest.items.map((item) => (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => setInspector({ mode: 'round', changeSetId: latest.id, itemId: item.id })}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs hover:border-info-foreground hover:bg-background',
            )}
            title="Inspect diff"
          >
            <span className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
              item.operation === 'Update' && 'bg-warning text-warning-foreground border border-warning-foreground',
              item.operation === 'Create' && 'bg-success text-success-foreground border border-success-foreground',
            )}>
              {item.operation === 'Update' && <TriangleAlert className="h-4 w-4" />}
              {item.operation === 'Create' && <CircleCheck className="h-4 w-4" />}
            </span>
            
            <span className="flex-1 truncate font-medium">
              {changeSetItemTitle(item)}
            </span>
            {latest.round > 1 && item.revision !== 'Unchanged' && (
              <span className="shrink-0 rounded-full bg-warning px-1.5 text-[9px] font-semibold uppercase text-warning-foreground">
                {item.revision === 'New' ? 'new' : 'revised'}
              </span>
            )}
            {isPending && canDecide && (
              <span className="flex shrink-0 gap-0.5">
                <button
                  type="button"
                  title="Stage — apply on approval"
                  aria-label={`Stage ${changeSetItemTitle(item)}`}
                  disabled={item.decision === 'Staged'}
                  className={cn(
                    'rounded p-1 text-muted-foreground hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent',
                    item.decision === 'Staged' && 'text-success-foreground',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    setDecision.mutate(
                      { changeSetId: latest.id, itemId: item.id, decision: 'Staged' },
                      decisionMutationOptions,
                    )
                  }}
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  title="Unstage — move back to Changes, skipped on approval"
                  aria-label={`Unstage ${changeSetItemTitle(item)}`}
                  disabled={item.decision !== 'Staged'}
                  className="rounded p-1 text-danger-foreground hover:bg-muted disabled:opacity-40"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDecision.mutate(
                      { changeSetId: latest.id, itemId: item.id, decision: 'Unstaged' },
                      decisionMutationOptions,
                    )
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        ))}
      </div>

      {isPending && (
        <div className="mt-2 rounded-md border border-dashed bg-muted/50 px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground">
          {latest.feedbackAvailable
            ? '💬 Not right yet? Type feedback in the chat — the agent warm-resumes and stages a new round. The new round arrives fully staged.'
            : 'No resumable agent for this gate — approve or reject in Overview.'}
          <div className="mt-1.5">
            <button type="button" className="text-info-foreground underline underline-offset-2 hover:text-info-foreground/70" onClick={onGoToOverview}>
              Approve / reject the gate in Overview →
            </button>
          </div>
        </div>
      )}

      {changeSetData.feedbackRounds.length > 0 && (
        <div className="mt-3">
          <div className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Feedback history ({changeSetData.feedbackRounds.length})
          </div>
          {changeSetData.feedbackRounds.map((f) => (
            <div key={f.id} className="flex gap-2 border-b border-dashed px-1 py-1.5 text-[11px] last:border-b-0">
              <span className="shrink-0 font-bold text-muted-foreground">R{f.supersededRound}</span>
              <span className="italic">"{f.message}"</span>
            </div>
          ))}
        </div>
      )}

      {previous.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between px-1 py-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Previous rounds</span>
            <Button
              size="sm"
              className="h-5 rounded-full px-2.5 text-[10px]"
              onClick={() => setInspector({ mode: 'all', changeSetId: latest.id, itemId: null })}
            >
              See All
            </Button>
          </div>
          <div className="flex flex-col gap-1">
            {previous.map((cs) => {
              const stagedCount = isReviewableRound(changeSetData.changeSets, cs)
                ? cs.items.filter((i) => i.decision === 'Staged').length
                : 0
              return (
                <button
                  key={cs.id}
                  type="button"
                  onClick={() => setInspector({ mode: 'round', changeSetId: cs.id, itemId: null })}
                  className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-left text-xs hover:border-info-foreground hover:bg-background"
                >
                  <span className="flex-1 font-medium">Round {cs.round}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {cs.items.filter((i) => i.operation === 'Create').length} new · {cs.items.filter((i) => i.operation === 'Update').length} upd
                    {stagedCount > 0 && <> · <b className="text-foreground">{stagedCount} staged</b></>}
                  </span>
                  <ChangeSetStatusBadge status={cs.status} />
                </button>
              )
            })}
          </div>
        </div>
      )}

      {inspector && (
        <ChangeSetDiffDialog
          key={`${inspector.mode}-${inspector.changeSetId}-${inspector.itemId ?? 'first'}`}
          open
          onOpenChange={(open) => !open && setInspector(null)}
          changeSets={changeSetData.changeSets}
          mode={inspector.mode}
          initialChangeSetId={inspector.changeSetId}
          initialItemId={inspector.itemId}
          canDecide={canDecide}
          canEdit={canDecide && (isRunInitiator || isAdmin)}
          onSetDecision={(changeSetId, itemId, decision, options) =>
            setDecision.mutate({ changeSetId, itemId, decision }, options)
          }
          onSetRoundDecision={(changeSetId, decision, options) =>
            setRoundDecision.mutate({ changeSetId, decision }, options)
          }
          onSaveProposedContent={(changeSetId, itemId, proposedValuesJson, options) =>
            editContent.mutate({ changeSetId, itemId, proposedValuesJson }, options)
          }
          isSaving={editContent.isPending}
        />
      )}
    </div>
  )
}

function NodeDetailTab({
  projectId,
  runId,
  nodeId,
  runStreamState,
}: {
  projectId: string
  runId: string
  nodeId: string
  runStreamState: ChatRunStreamState
}) {
  const [subTab, setSubTab] = useState<'inputs' | 'outputs' | 'logs'>('outputs')
  const tenantKey = useTenantKey()

  const { data, isLoading } = useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'nodes', nodeId),
    queryFn: () => workflowRunsHitlApi.getNodeDetail(projectId, runId, nodeId),
    staleTime: 2_000,
    refetchInterval: runStreamState.nodeStatuses[nodeId] === 'Running' ? 3_000 : false,
  })

  if (isLoading) return <div className="p-3 text-sm text-muted-foreground">Loading node…</div>
  if (!data) return <div className="p-3 text-sm text-muted-foreground">Node not found.</div>

  const liveStatus = runStreamState.nodeStatuses[nodeId] ?? data.status

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-muted/30 px-3 py-2">
        <div className="truncate text-sm font-bold">{data.name}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          <KindBadge isLlm={data.isLlm} />
          <span className="font-mono">{data.taskType}</span>
          <StatusPill status={liveStatus} />
          {data.durationMs != null && <span className="font-mono">· {formatDurationMs(data.durationMs)}</span>}
        </div>
      </div>

      <div className="flex border-b bg-background text-xs">
        {(['inputs', 'outputs', 'logs'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSubTab(s)}
            className={[
              'px-3 py-2 font-semibold',
              subTab === s
                ? 'border-b-2 border-info-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 text-xs">
        {subTab === 'inputs' && <JsonBlock json={data.inputsJson} fallback="No inputs." />}
        {subTab === 'outputs' && (
          data.outputsJson ? <OutputsBlock outputsJson={data.outputsJson} /> : (
            <div className="italic text-muted-foreground">
              Pending — node not yet completed.
            </div>
          )
        )}
        {subTab === 'logs' && (
          <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
            {data.logs || '(no logs)'}
          </pre>
        )}
      </div>
    </div>
  )
}

function OutputsBlock({ outputsJson }: { outputsJson: string }) {
  const entries = parseOutputsJson(outputsJson)

  if (entries === null) return <JsonBlock json={outputsJson} fallback="Empty." />
  if (entries.length === 0) return <div className="italic text-muted-foreground">Empty.</div>

  return (
    <div className="space-y-3" data-testid="chat-node-outputs">
      {entries.map((entry) => (
        <div key={entry.key} className="rounded border p-2">
          <div className="mb-1 font-semibold">{entry.key}</div>
          <div className="relative">
            {hasCopyableContent(entry.value) && (
              <CopyButton text={entry.value} className="absolute right-0 top-0 z-10" tooltipLabel={`Copy ${entry.key}`} />
            )}
            {renderOutputValue(entry.key, entry.value)}
          </div>
        </div>
      ))}
    </div>
  )
}

function JsonBlock({ json, fallback }: { json: string | null; fallback: string }) {
  if (!json) return <div className="italic text-muted-foreground">{fallback}</div>
  let pretty = json
  try {
    pretty = JSON.stringify(JSON.parse(json), null, 2)
  } catch {
    /* keep as-is */
  }
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
      {pretty}
    </pre>
  )
}


function StatusPill({ status }: { status: string }) {
  const cls = pillClassForStatus(status);
  const Icon = STATUS_ICONS[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold tracking-wide ${cls}`}>
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {status}
    </span>
  )
}

const STATUS_ICONS: Record<string, LucideIcon> = {
  Completed: CheckCircle,
  Running: CirclePlay,
  AwaitingUserInput: MessageSquareWarning,
  AwaitingApproval: AlertTriangle,
  Failed: XCircle,
  Skipped: SkipForward,
  RetryPending: Repeat,
}

function NodeStatusIcon({ status }: { status: string }) {
  const colorClass =
    status === "Completed"
      ? "bg-success-foreground"
      : status === "Running"
        ? "bg-info-foreground"
        : status === "AwaitingUserInput"
          ? "bg-warning-foreground"
          : status === "Failed"
            ? "bg-danger-foreground"
            : "bg-muted-foreground/40";
  return <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colorClass}`} />;
}

function NodeStatusBadgeIcon({ status }: { status: string }) {
  const Icon = STATUS_ICONS[status] ?? History
  return (
    <span
      title={status}
      aria-label={status}
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
        bgClassForStatus(status),
        textClassForStatus(status),
      )}
    >
      <Icon className="h-3 w-3" />
    </span>
  )
}

function bgClassForStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-success border-success-foreground'
    case 'Running':
      return 'bg-info border-info-foreground'
    case 'AwaitingUserInput':
    case 'AwaitingApproval':
      return 'bg-warning border-warning-foreground'
    case 'Failed':
      return 'bg-danger border-danger-foreground'
    case 'Skipped':
      return 'bg-neutral border-neutral-foreground'
    case 'RetryPending':
      return 'bg-new border-new-foreground'
    default:
      return 'bg-muted border-muted-foreground'
  }
}

function textClassForStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'text-success-foreground'
    case 'Running':
      return 'text-info-foreground'
    case 'AwaitingUserInput':
    case 'AwaitingApproval':
      return 'text-warning-foreground'
    case 'Failed':
      return 'text-danger-foreground'
    case 'Skipped':
      return 'text-neutral-foreground'
    case 'RetryPending':
      return 'text-new-foreground'
    default:
      return 'text-muted-foreground'
  }
}

function KindBadge({ isLlm }: { isLlm: boolean }) {
  return (
    <span
      className={[
        'rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
        isLlm ? 'bg-info text-info-foreground' : 'bg-neutral text-neutral-foreground',
      ].join(' ')}
    >
      {isLlm ? 'LLM' : 'deterministic'}
    </span>
  )
}

function pillClassForStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'bg-success text-success-foreground border border-success-foreground'
    case 'Running':
      return 'bg-info text-info-foreground border border-info-foreground'
    case 'AwaitingUserInput':
      return 'bg-warning text-warning-foreground border border-warning-foreground'
    case 'AwaitingApproval':
      return 'bg-warning text-warning-foreground border border-warning-foreground'
    case 'Failed':
      return 'bg-danger text-danger-foreground border border-danger-foreground'
    case 'Skipped':
      return 'bg-neutral text-neutral-foreground border border-neutral-foreground'
    case 'RetryPending':
      return 'bg-new text-new-foreground border border-new-foreground'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function borderClassForStatus(status: string): string {
  switch (status) {
    case 'Completed':
      return 'border-success-foreground'
    case 'Running':
      return 'border-info-foreground'
    case 'AwaitingUserInput':
    case 'AwaitingApproval':
      return 'border-warning-foreground'
    case 'Failed':
      return 'border-danger-foreground'
    case 'Skipped':
      return 'border-neutral-foreground'
    case 'RetryPending':
      return 'border-new-foreground'
    default:
      return 'border-border'
  }
}

function nodeNameById(
  run: WorkflowRunSummary,
  runStreamState: ChatRunStreamState,
  nodeId: string,
): string | null {
  void runStreamState
  void run
  return nodeId.slice(0, 8)
}

export type { Props as ChatRunSidebarProps }
